import { LazyStore, reactive } from '@devim-front/store';
import { action, computed, observable } from 'mobx';

import { applyFetchable } from 'modules/common/stores';

import {
  LoanModel,
  NotFoundError,
  PayErrors,
  PhoneModel,
  SmsLimitError,
  WrongCodeError,
} from '../types';
import { CompatService } from 'services/CompatService';
import { RpcError } from 'services/RpcService/errors/RpcError';
import { RpcErrorCode } from 'services/RpcService/types/RpcErrorCode';
import { PaymentData } from 'services/RpcService/types/PaymentData';
import { PaymentType } from '../../repay';
import { ServiceType } from 'services/RpcService/types/ServiceType';
import { PaymentMethod } from 'services/RpcService/types/PaymentMethod';
import { Service } from '../services/Service';
import { PaymentProvider } from 'services/RpcService/types/PaymentProvider';
import { PaymentMethodType } from 'services/RpcService/types/PaymentMethodType';

@reactive
export class Store extends applyFetchable(LazyStore) {
  /**
   * Телефон, который ввёл пользователь на экране оплаты
   */
  @observable
  public phone?: PhoneModel;

  /**
   * Форма поиска по номеру займа
   */
  @observable
  public loanForm?: LoanModel;

  /**
   * @inheritDoc
   */
  @observable
  public code?: string;

  /**
   * Указывает, что в данный момент происходит асинхронная операция.
   */
  @observable
  public pending: boolean = false;

  /**
   * Токен для запроса оплаты
   * @private
   */
  @observable
  public token: string = '';

  /**
   * Указывает на то, что временный пароль отправлен
   */
  @observable
  public isCodeSend: boolean = false;

  /**
   * Указывает на то, что отправлен номер займа
   */
  @observable
  public isLoanSend: boolean = false;

  /**
   * Указывает на то, что отправлен номер телефона
   */
  @observable
  public isPhoneSend: boolean = false;

  /**
   * Текущее количество секунд, через которое можно будет отправить код подтверждения повторно.
   */
  @observable
  public countdown: number = 0;

  /**
   * Сумма оплаты по займу
   */
  @observable
  public amountFull: number = 0;

  /**
   * Минимальная сумма платежа
   */
  @observable
  public amountMinimal: number = 0;

  /**
   * Сумма просроченного платежа
   */
  @observable
  public amountExpired: number = 0;

  /**
   * Сумма произвольного платежа
   */
  @observable
  public amountAny: number = 0;

  /**
   * Является ли клиент просрочкой
   */
  @observable
  public isExpired: boolean = false;

  /**
   * Выбран ли тип оплаты
   */
  @observable
  public isPaymentTypeChosen: boolean = false;

  /**
   * Методы оплаты
   */
  @observable
  public paymentMethods: PaymentMethod[] = [];

  /*
   * Ошибка, которую вернул сервер после последней операции.
   */
  @observable
  public error?: any;

  /**
   * Идентификатор setInterval при отправке кода подтверждения.
   */
  private interval?: any;

  /**
   * Ссылка на сервис RPC.
   */
  private get rpc() {
    return CompatService.get(this).rpcService;
  }

  /**
   * Ссылка на сервис оплаты.
   */
  private get service() {
    return Service.get(this);
  }

  /**
   * Сообщение об ошибке, которое должно быть отображено на форме в данный
   * момент.
   */
  @computed
  public get errorMessage(): string | null {
    const { error } = this;

    if (error === PayErrors.TO_MUCH_AMOUNT) {
      return 'Сумма не может быть больше общей задолжности';
    }

    if (error === PayErrors.TO_MUCH_AMOUNT_LOAN) {
      return 'Для данного типа оплаты максимальная сумма 15000';
    }

    if (error instanceof NotFoundError) {
      return 'Заём не найден. Проверьте данные и повторите попытку.';
    }

    if (error instanceof SmsLimitError) {
      return 'Лимит смс превышен';
    }

    if (error instanceof WrongCodeError) {
      return 'Введён неверный код';
    }

    if (error) {
      return `Неизвестная ошибка`;
    }

    return null;
  }

  /**
   * Высчитываем стоит ли показывать форму полной суммы оплаты по задолжности
   */
  @computed
  public get isHideFullPay(): boolean {
    return Boolean(
      this.isLoanSend &&
        this.amountFull &&
        Number(this.amountFull) > 1500000 &&
        !this.isExpired,
    );
  }

  /**
   * @inheritDoc
   */
  fetch() {
    const phone = this.service.readPaymentPhone();
    const token = this.service.readPaymentToken();
    const amountFull = this.service.readPaymentAmountFull();
    const amountMin = this.service.readPaymentAmountMinimal();
    const amountExpired = this.service.readPaymentAmountExpired();
    const amountAny = this.service.readPaymentAmountAny();
    const isExpired = this.service.readPaymentIsExpired();
    const isPhoneSend = this.service.readPaymentIsPhoneSend();
    const isCodeSend = this.service.readPaymentIsCodeSend();
    const isLoanSend = this.service.readPaymentIsLoanSend();
    const isPaymentTypeChosen = this.service.readPaymentIsTypeChosen();

    return () => {
      this.setPhone(phone);
      this.setToken(token);
      this.setAmountFull(amountFull);
      this.setAmountMinimal(amountMin);
      this.setAmountExpired(amountExpired);
      this.setAmountAny(amountAny);
      this.setIsExpired(isExpired);
      this.setIsPhoneSend(isPhoneSend);
      this.setIsCodeSend(isCodeSend);
      this.setIsLoanSend(isLoanSend);
      this.setIsTypeChosen(isPaymentTypeChosen);
    };
  }

  /**
   * Установка токена оплаты на актуальный
   * @param token
   */
  @action
  private setToken(token: string) {
    this.service.savePaymentToken(token);
    this.token = token;
  }

  /**
   * Установка суммы задолженности
   * @param amount
   */
  @action
  private setAmountFull(amount: number) {
    this.service.savePaymentAmountFull(amount);
    this.amountFull = amount;
  }

  /**
   * Установка минимального платежа
   * @param amount
   */
  @action
  private setAmountMinimal(amount: number) {
    this.service.savePaymentAmountMinimal(amount);
    this.amountMinimal = amount;
  }

  /**
   * Установка просроченного платежа
   * @param amount
   */
  @action
  private setAmountExpired(amount: number) {
    this.service.savePaymentAmountExpired(amount);
    this.amountExpired = amount;
  }

  /**
   * Установка произвольного платежа
   * @param amount
   */
  @action
  public setAmountAny(amount: number) {
    this.service.savePaymentAmountAny(amount);
    this.amountAny = amount;
  }

  /**
   * Установка флага просрочен или нет
   * @param isExpired
   */
  @action
  private setIsExpired(isExpired: boolean) {
    this.service.savePaymentIsExpired(isExpired);
    this.isExpired = isExpired;
  }

  /**
   * Установка флага введен телефон или нет
   * @param isPhoneSend
   */
  @action
  private setIsPhoneSend(isPhoneSend: boolean) {
    this.service.savePaymentIsPhoneSend(isPhoneSend);
    this.isPhoneSend = isPhoneSend;
  }

  /**
   * Установка флага отправлен код на телефон или нет
   * @param isCodeSend
   */
  @action
  private setIsCodeSend(isCodeSend: boolean) {
    this.service.savePaymentIsCodeSend(isCodeSend);
    this.isCodeSend = isCodeSend;
  }

  /**
   * Установка флага отправлен ли номер займ
   * @param isLoanSend
   */
  @action
  private setIsLoanSend(isLoanSend: boolean) {
    this.service.savePaymentIsLoanSend(isLoanSend);
    this.isLoanSend = isLoanSend;
  }

  /**
   * Функция, задающая параметр phone
   * @param phone
   * @private
   */
  @action
  private setPhone(phone?: PhoneModel) {
    this.phone = phone;
    this.service.savePaymentPhone(this.phone);
  }

  /**
   * Метод задает значение
   * @param flag
   * @private
   */
  @action
  private setIsTypeChosen(flag: boolean) {
    this.isPaymentTypeChosen = flag;
    this.service.savePaymentIsTypeChosen(flag);
  }

  /**
   * Метод записывает методы оплаты в стор
   * @param methods
   * @private
   */
  @action
  private setPaymentMethods(methods: PaymentMethod[]) {
    this.paymentMethods = methods;
  }

  /**
   * Отменить ввод одноразового пароля
   */
  @action
  public async cancelCode() {
    this.setIsCodeSend(false);
    this.setIsPhoneSend(false);
    this.setError(undefined);
  }

  /**
   *
   */
  @action
  public resetCache() {
    this.setAmountFull(0);
    this.setAmountMinimal(0);
    this.setAmountExpired(0);
    this.setIsExpired(false);
    this.setToken('');
    this.setIsPhoneSend(false);
    this.setIsCodeSend(false);
    this.setIsLoanSend(false);
    this.setPhone(undefined);
    this.setIsTypeChosen(false);
    this.service.clearCache();
  }

  /**
   * Вызывает отправку кода подтверждения операции.
   * @param phoneNumber Номер телефона для отправки кода.
   */
  private async sendCode(phoneNumber: string) {
    try {
      await this.rpc.loginPaymentSendConfirmationCode(phoneNumber);
    } catch (error) {
      if (RpcError.isType(error, RpcErrorCode.ClientNotFoundByPhone)) {
        throw new NotFoundError(error);
      }

      if (RpcError.isType(error, RpcErrorCode.SmsLimitExceeded)) {
        throw new SmsLimitError(error);
      }

      throw new Error(error as any);
    }
  }

  /**
   * Функция отправки формы ввода телефона
   * @param phoneNumber Номер телефона.
   */
  @action
  public async submitPhoneForm(phoneNumber: string) {
    await this.process(async () => {
      const phoneModel: PhoneModel = { value: phoneNumber };
      this.setPhone(phoneModel);

      await this.sendCode(phoneNumber);

      this.setIsCodeSend(true);
      this.setIsPhoneSend(true);
      this.setIsLoanSend(false);
      this.startCountdown();
    });
  }

  /**
   * Переотправка кода подтверждения
   */
  @action
  public async resendCode() {
    await this.process(async () => {
      if (!this.phone) {
        this.setIsCodeSend(false);
        this.setIsPhoneSend(false);
        return;
      }
      await this.sendCode(this.phone.value);
      this.startCountdown();
    });
  }

  /**
   * Подтверждение телефона по одноразовому паролю
   */
  @action
  public async submitConfirmForm(code: string, callback: () => void) {
    this.service.clearCache();

    await this.process(async () => {
      if (!this.phone) {
        throw new Error();
      }

      let response: PaymentData;
      try {
        response = await this.rpc.loginPaymentByPhone(this.phone.value, code);
      } catch (e) {
        throw new WrongCodeError(e as any);
      }

      this.setAmountFull(response.paymentSummary.earlyRepayment.amount);
      this.setAmountMinimal(response.paymentSummary.nextPayment.amount);
      this.setAmountExpired(response.paymentSummary.debt.amount);
      this.setIsExpired(Boolean(response.paymentSummary.debt.amount));
      this.setToken(response.token);
      callback();
    });
  }

  /**
   * Функция отправки формы номера займа
   * @param loanForm
   * @param callback
   */
  @action
  public submitLoanForm(loanForm: LoanModel, callback: () => void) {
    this.service.clearCache();

    this.process(async () => {
      let response: PaymentData;
      try {
        response = await this.rpc.loginPaymentByLoan(
          loanForm.loanNumber,
          loanForm.birthDate,
        );
      } catch (e) {
        if (RpcError.isType(e, RpcErrorCode.LoanNotFound)) {
          throw new NotFoundError(e);
        }

        throw new Error(e as any);
      }

      this.setAmountFull(response.paymentSummary.earlyRepayment.amount);
      this.setAmountMinimal(response.paymentSummary.nextPayment.amount);
      this.setAmountExpired(response.paymentSummary.debt.amount);
      this.setIsExpired(Boolean(response.paymentSummary.debt.amount));
      this.setToken(response.token);
      this.setIsLoanSend(true);
      this.setIsPhoneSend(false);
      this.setIsCodeSend(false);
      callback();
    });
  }

  /**
   * Функция отправки формы оплаты
   */
  @action
  public async submitPayForm(amount: number, type: PaymentType) {
    this.setError(undefined);

    // Кейс просрочки по номеру телефона
    if (
      this.amountFull &&
      this.isExpired &&
      this.isPhoneSend &&
      amount > Number(this.amountFull)
    ) {
      this.setError(PayErrors.TO_MUCH_AMOUNT);
      return;
    }

    // Кейс просрочки по номеру займа
    if (
      this.amountFull &&
      this.isExpired &&
      this.isLoanSend &&
      amount > Number(this.amountFull)
    ) {
      this.setError(PayErrors.TO_MUCH_AMOUNT);
      return;
    }

    if (this.isLoanSend && amount > 1500000) {
      this.setError(PayErrors.TO_MUCH_AMOUNT_LOAN);
      return;
    }

    if (type === PaymentType.ANY) {
      this.setAmountAny(amount);
    }

    this.service.savePaymentType(type);
    this.setIsTypeChosen(true);
  }

  /**
   * Метод получает методы оплаты с сервера и записывает в стор
   */
  @action
  public async fetchPaymentMethods() {
    const type = this.service.readPaymentType();

    let amount: number | undefined;

    if (type === PaymentType.NEXT) {
      amount = this.amountMinimal;
    }

    if (type === PaymentType.EARLY) {
      amount = this.amountFull;
    }

    if (type === PaymentType.ANY) {
      amount = this.amountAny;
    }

    if (typeof amount !== 'number') {
      return;
    }

    const methods = await this.rpc.paymentGetMethodsByToken(
      ServiceType.Loan,
      amount,
      this.token,
    );

    this.setPaymentMethods(methods);
  }

  /**
   * Метод возвращает на форму оплаты
   */
  @action
  public backToPayForm() {
    this.setIsTypeChosen(false);
  }

  /**
   * Метод выбора способа оплаты
   */
  @action
  public async choosePaymentMethod(
    provider: PaymentProvider,
    method: PaymentMethodType,
  ) {
    const payments = {
      [PaymentType.EARLY]: this.amountFull,
      [PaymentType.NEXT]: this.amountMinimal,
      [PaymentType.ANY]: this.amountAny,
    };
    const paymentType = this.service.readPaymentType();
    const amount = payments[paymentType];

    await this.process(async () => {
      const { url } = await this.rpc.paymentGetInfoByToken(
        provider,
        method,
        amount,
        this.token!,
      );
      document.location.href = url;
    });
  }

  /**
   * Функция, задающая параметр pending
   */
  @action
  private setPending(pending: boolean) {
    this.pending = pending;
  }

  /**
   * Функция, задающая параметр pending
   */
  @action
  private setError(err?: PayErrors) {
    this.error = err;
  }

  /**
   * Осуществляет вызов асинхронной операции.
   */
  private async process(worker: () => Promise<any>) {
    try {
      this.setPending(true);

      await worker();

      this.setError(undefined);
    } catch (error) {
      this.setError(error as any);
    } finally {
      this.setPending(false);
    }
  }

  /**
   * Функция задающая параметр countdown
   */
  @action
  private setCountdown(value: number) {
    this.countdown = value;
  }

  /**
   * Функция отсчитывающая тайм-аут, до отправки нового кода подтверждения.
   */
  @action
  private countdownTick() {
    this.setCountdown(this.countdown - 1);

    if (this.countdown === 0) {
      clearInterval(this.interval);
    }
  }

  /**
   *
   */
  @action
  public startCountdown() {
    if (this.phone == null) {
      throw new Error(`this.phone is undefined`);
    }

    if (this.countdown > 0) {
      return;
    }

    this.setCountdown(60);
    this.interval = setInterval(() => this.countdownTick(), 1000);
  }
}
